import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { calendarService } from '../../services/calendarService';
import { realtimeService } from '../../services/realtimeService';
import { parseISO, startOfMonth, endOfMonth, isAfter, isBefore } from 'date-fns';
import MetricsOverview from './components/MetricsOverview';
import ConsultationGrid from './components/ConsultationGrid';
import FilterPanel from './components/FilterPanel';
import Toast from '../../components/ui/Toast';

const ConsultationManagementConsole = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    serviceType: 'all',
    searchTerm: ''
  });
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadBookings();
    setupRealtimeSubscription();

    return () => {
      if (realtimeChannel) {
        realtimeService?.unsubscribe(realtimeChannel);
      }
    };
  }, [user, userProfile, navigate]);

  useEffect(() => {
    applyFilters();
  }, [bookings, filters]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await calendarService?.getAllBookings();
      setBookings(data || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bookings];

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(b => b?.status === filters?.status);
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      filtered = filtered?.filter(b => {
        const bookingDate = parseISO(b?.scheduledStart);
        if (filters?.dateRange === 'upcoming') {
          return isAfter(bookingDate, now);
        } else if (filters?.dateRange === 'past') {
          return isBefore(bookingDate, now);
        } else if (filters?.dateRange === 'this_month') {
          return isAfter(bookingDate, monthStart) && isBefore(bookingDate, monthEnd);
        }
        return true;
      });
    }

    // Service type filter
    if (filters?.serviceType !== 'all') {
      filtered = filtered?.filter(b => b?.serviceInterest === filters?.serviceType);
    }

    // Search filter
    if (filters?.searchTerm) {
      const searchLower = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(b => 
        b?.clientName?.toLowerCase()?.includes(searchLower) ||
        b?.clientEmail?.toLowerCase()?.includes(searchLower) ||
        b?.company?.toLowerCase()?.includes(searchLower) ||
        b?.title?.toLowerCase()?.includes(searchLower)
      );
    }

    setFilteredBookings(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedBookings?.length === 0) {
      alert('Please select consultations to update');
      return;
    }

    if (!window.confirm(`Update ${selectedBookings?.length} consultation(s) to ${newStatus}?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedBookings?.map(id => 
          calendarService?.updateBooking(id, { status: newStatus })
        )
      );
      setSelectedBookings([]);
      await loadBookings();
    } catch (err) {
      setError(err?.message || 'Failed to update consultations');
    }
  };

  const handleSelectBooking = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev?.includes(bookingId)) {
        return prev?.filter(id => id !== bookingId);
      }
      return [...prev, bookingId];
    });
  };

  const handleSelectAll = () => {
    if (selectedBookings?.length === filteredBookings?.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings?.map(b => b?.id));
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await calendarService?.updateBooking(bookingId, { status: newStatus });
      await loadBookings();
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    }
  };

  const calculateMetrics = () => {
    const total = bookings?.length || 0;
    const pending = bookings?.filter(b => b?.status === 'pending')?.length || 0;
    const confirmed = bookings?.filter(b => b?.status === 'confirmed')?.length || 0;
    const completed = bookings?.filter(b => b?.status === 'completed')?.length || 0;
    const cancelled = bookings?.filter(b => b?.status === 'cancelled')?.length || 0;
    const conversionRate = total > 0 ? ((confirmed + completed) / total * 100)?.toFixed(1) : 0;
    
    return {
      total,
      pending,
      confirmed,
      completed,
      cancelled,
      conversionRate
    };
  };

  const setupRealtimeSubscription = () => {
    const channel = realtimeService?.subscribeToConsultationBookings(
      (newBooking) => {
        console.log('New consultation booking:', newBooking);
        setBookings(prev => [{
          id: newBooking?.id,
          clientId: newBooking?.client_id,
          slotId: newBooking?.slot_id,
          title: newBooking?.title,
          description: newBooking?.description,
          clientName: newBooking?.client_name,
          clientEmail: newBooking?.client_email,
          clientPhone: newBooking?.client_phone,
          company: newBooking?.company,
          serviceInterest: newBooking?.service_interest,
          scheduledStart: newBooking?.scheduled_start,
          scheduledEnd: newBooking?.scheduled_end,
          durationMinutes: newBooking?.duration_minutes,
          status: newBooking?.status,
          meetingNotes: newBooking?.meeting_notes,
          createdAt: newBooking?.created_at,
          updatedAt: newBooking?.updated_at
        }, ...prev]);
        setToast({
          message: `New consultation booked: ${newBooking?.client_name}`,
          type: 'success'
        });
      },
      (updatedBooking) => {
        console.log('Consultation booking updated:', updatedBooking);
        setBookings(prev => 
          prev?.map(booking => 
            booking?.id === updatedBooking?.id ? {
              id: updatedBooking?.id,
              clientId: updatedBooking?.client_id,
              slotId: updatedBooking?.slot_id,
              title: updatedBooking?.title,
              description: updatedBooking?.description,
              clientName: updatedBooking?.client_name,
              clientEmail: updatedBooking?.client_email,
              clientPhone: updatedBooking?.client_phone,
              company: updatedBooking?.company,
              serviceInterest: updatedBooking?.service_interest,
              scheduledStart: updatedBooking?.scheduled_start,
              scheduledEnd: updatedBooking?.scheduled_end,
              durationMinutes: updatedBooking?.duration_minutes,
              status: updatedBooking?.status,
              meetingNotes: updatedBooking?.meeting_notes,
              createdAt: updatedBooking?.created_at,
              updatedAt: updatedBooking?.updated_at
            } : booking
          )
        );
        setToast({
          message: `Consultation updated: ${updatedBooking?.client_name} - ${updatedBooking?.status}`,
          type: 'info'
        });
      },
      (deletedBooking) => {
        console.log('Consultation booking deleted:', deletedBooking);
        setBookings(prev => prev?.filter(booking => booking?.id !== deletedBooking?.id));
        setToast({
          message: `Consultation cancelled: ${deletedBooking?.client_name}`,
          type: 'warning'
        });
      }
    );
    setRealtimeChannel(channel);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading consultation data...</p>
          </div>
        </div>
      </div>
    );
  }

  const metrics = calculateMetrics();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
      <Helmet>
        <title>Consultation Management Console | Northstar Analytics</title>
        <meta name="description" content="Administrative dashboard for managing all scheduled consultations with status tracking and follow-up workflows" />
      </Helmet>

      <Header />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Consultation Management Console
            </h1>
            <p className="text-lg text-muted-foreground">
              Centralized command center for managing all consultation activities
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Metrics Overview */}
          <MetricsOverview metrics={metrics} />

          {/* Filter Panel */}
          <FilterPanel 
            filters={filters}
            onFilterChange={handleFilterChange}
            bookings={bookings}
          />

          {/* Bulk Actions */}
          {selectedBookings?.length > 0 && (
            <div className="bg-white rounded-xl shadow-elevation-1 p-4 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Icon name="CheckSquare" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {selectedBookings?.length} consultation(s) selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkStatusUpdate('confirmed')}
                  >
                    Mark Confirmed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkStatusUpdate('completed')}
                  >
                    Mark Completed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkStatusUpdate('cancelled')}
                  >
                    Mark Cancelled
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedBookings([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Consultation Grid */}
          <ConsultationGrid
            bookings={filteredBookings}
            selectedBookings={selectedBookings}
            onSelectBooking={handleSelectBooking}
            onSelectAll={handleSelectAll}
            onStatusUpdate={handleStatusUpdate}
            onRefresh={loadBookings}
          />
        </div>
      </div>
    </div>
  );
};

export default ConsultationManagementConsole;